import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@tradermind/shared/stores';
import { ROUTES } from '@tradermind/shared/constants';
import { Spin } from 'antd';

import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

import DashboardPage from './pages/dashboard/DashboardPage';
import TradeListPage from './pages/trades/TradeListPage';
import TradeDetailPage from './pages/trades/TradeDetailPage';
import TradeFormPage from './pages/trades/TradeFormPage';
import ImportTradesPage from './pages/trades/ImportTradesPage';
import ProfitCalendarPage from './pages/trades/ProfitCalendarPage';

import EmotionListPage from './pages/emotions/EmotionListPage';
import EmotionFormPage from './pages/emotions/EmotionFormPage';
import EmotionAnalysisPage from './pages/emotions/EmotionAnalysisPage';

import PreTradeChecklistPage from './pages/checklist/PreTradeChecklistPage';
import ChecklistResultPage from './pages/checklist/ChecklistResultPage';
import CooldownPage from './pages/checklist/CooldownPage';

import ReportsPage from './pages/reports/ReportsPage';
import ProfilePage from './pages/profile/ProfilePage';
import ChangePasswordPage from './pages/profile/ChangePasswordPage';
import SettingsPage from './pages/settings/SettingsPage';

import SupportPage from './pages/support/SupportPage';
import CreateTicketPage from './pages/support/CreateTicketPage';
import TicketDetailPage from './pages/support/TicketDetailPage';

import StrategyListPage from './pages/strategies/StrategyListPage';
import StrategyFormPage from './pages/strategies/StrategyFormPage';
import StrategyStatsPage from './pages/strategies/StrategyStatsPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.AUTH.LOGIN} replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
}

function App() {
  // Hydration is now handled automatically by setAuthStorage in main.tsx
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={ROUTES.AUTH.LOGIN}
          element={
            <PublicRoute>
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.AUTH.REGISTER}
          element={
            <PublicRoute>
              <AuthLayout>
                <RegisterPage />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.AUTH.FORGOT_PASSWORD}
          element={
            <PublicRoute>
              <AuthLayout>
                <ForgotPasswordPage />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.AUTH.RESET_PASSWORD}
          element={
            <PublicRoute>
              <AuthLayout>
                <ResetPasswordPage />
              </AuthLayout>
            </PublicRoute>
          }
        />

        <Route path={ROUTES.LEGAL.TERMS} element={<TermsOfService />} />
        <Route path={ROUTES.LEGAL.PRIVACY} element={<PrivacyPolicy />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={ROUTES.DASHBOARD} replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          <Route path="trades" element={<TradeListPage />} />
          <Route path="trades/new" element={<TradeFormPage />} />
          <Route path="trades/:id" element={<TradeDetailPage />} />
          <Route path="trades/:id/edit" element={<TradeFormPage />} />
          <Route path="trades/import" element={<ImportTradesPage />} />
          <Route path="profit-calendar" element={<ProfitCalendarPage />} />

          <Route path="emotions" element={<EmotionListPage />} />
          <Route path="emotions/new" element={<EmotionFormPage />} />
          <Route path="emotions/analysis" element={<EmotionAnalysisPage />} />

          <Route path="checklist/pre-trade" element={<PreTradeChecklistPage />} />
          <Route path="checklist/result/:id" element={<ChecklistResultPage />} />
          <Route path="cooldown" element={<CooldownPage />} />

          <Route path="strategies" element={<StrategyListPage />} />
          <Route path="strategies/new" element={<StrategyFormPage />} />
          <Route path="strategies/:id/edit" element={<StrategyFormPage />} />
          <Route path="strategies/:id/stats" element={<StrategyStatsPage />} />

          <Route path="reports/*" element={<ReportsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="profile/change-password" element={<ChangePasswordPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="support/new" element={<CreateTicketPage />} />
          <Route path="support/:id" element={<TicketDetailPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
